import { Fragment } from 'react';
import type { CSSProperties } from 'react';
import type { LocalAdjustments, MaskKind } from '@lumos/core';
import { useSelectedPhoto, useStore } from '../../state/store.ts';
import { MAX_MASKS } from '../../engine/shaders.ts';
import { GroupLabel, Panel } from '../controls/Panel.tsx';
import { Slider } from '../controls/Slider.tsx';
import { Tip } from '../controls/Tooltip.tsx';

const ROW: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 5,
  margin: '4px 0 2px',
};

const LIST: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  margin: '4px 0',
};

const NAME_BUTTON: CSSProperties = { flex: 1, minWidth: 0, textAlign: 'left' };

const KIND_LABEL: Record<MaskKind, string> = {
  radial: 'Radial',
  linear: 'Linear',
  luminance: 'Luminance',
  color: 'Colour',
};

const ADD_BUTTONS: { kind: MaskKind; label: string }[] = [
  { kind: 'radial', label: 'Radial' },
  { kind: 'linear', label: 'Linear' },
  { kind: 'luminance', label: 'Luminance range' },
  { kind: 'color', label: 'Colour range' },
];

const HUE_TRACK = `linear-gradient(90deg, ${Array.from(
  { length: 13 },
  (_, index) => `hsl(${index * 30} 80% 52%)`,
).join(', ')})`;

interface GeometryField<K extends string> {
  key: K;
  label: string;
  min: number;
  max: number;
  step: number;
  neutral: number;
  unit?: string;
  gradient?: string;
  help: string;
}

const RADIAL_FIELDS: GeometryField<'cx' | 'cy' | 'rx' | 'ry' | 'angle'>[] = [
  {
    key: 'cx',
    label: 'Centre X',
    min: 0,
    max: 1,
    step: 0.002,
    neutral: 0.5,
    help: 'Horizontal centre, as a fraction of the frame.',
  },
  {
    key: 'cy',
    label: 'Centre Y',
    min: 0,
    max: 1,
    step: 0.002,
    neutral: 0.5,
    help: 'Vertical centre, measured down from the top.',
  },
  {
    key: 'rx',
    label: 'Radius X',
    min: 0.01,
    max: 1,
    step: 0.005,
    neutral: 0.32,
    help: 'Half-width. Make it bigger than the subject and let the feather do the blending.',
  },
  {
    key: 'ry',
    label: 'Radius Y',
    min: 0.01,
    max: 1,
    step: 0.005,
    neutral: 0.32,
    help: 'Half-height of the ellipse.',
  },
  {
    key: 'angle',
    label: 'Angle',
    min: -180,
    max: 180,
    step: 1,
    neutral: 0,
    unit: '°',
    help: 'Turns the ellipse. Useful for a face on a diagonal.',
  },
];

const LINEAR_FIELDS: GeometryField<'x1' | 'y1' | 'x2' | 'y2'>[] = [
  {
    key: 'x1',
    label: 'Start X',
    min: 0,
    max: 1,
    step: 0.002,
    neutral: 0.5,
    help: 'Where the gradient is at full strength.',
  },
  {
    key: 'y1',
    label: 'Start Y',
    min: 0,
    max: 1,
    step: 0.002,
    neutral: 0.15,
    help: 'Full-strength edge, measured down from the top.',
  },
  {
    key: 'x2',
    label: 'End X',
    min: 0,
    max: 1,
    step: 0.002,
    neutral: 0.5,
    help: 'Where the gradient has faded to nothing.',
  },
  {
    key: 'y2',
    label: 'End Y',
    min: 0,
    max: 1,
    step: 0.002,
    neutral: 0.6,
    help: 'Zero-strength edge. A long run keeps a sky gradient invisible.',
  },
];

const LUMINANCE_FIELDS: GeometryField<'low' | 'high' | 'lowFeather' | 'highFeather'>[] = [
  {
    key: 'low',
    label: 'Low',
    min: 0,
    max: 1,
    step: 0.005,
    neutral: 0,
    help: 'Darkest brightness the mask includes.',
  },
  {
    key: 'high',
    label: 'High',
    min: 0,
    max: 1,
    step: 0.005,
    neutral: 0.5,
    help: 'Brightest brightness included. 0 to 0.35 is a shadows mask, 0.7 to 1 a highlights mask.',
  },
  {
    key: 'lowFeather',
    label: 'Low feather',
    min: 0,
    max: 0.5,
    step: 0.005,
    neutral: 0.1,
    help: 'How gradually the mask fades in at the dark end. Too little and smooth gradients band.',
  },
  {
    key: 'highFeather',
    label: 'High feather',
    min: 0,
    max: 0.5,
    step: 0.005,
    neutral: 0.15,
    help: 'How gradually the mask fades out at the bright end.',
  },
];

const COLOR_FIELDS: GeometryField<'hue' | 'hueRange' | 'minSaturation'>[] = [
  {
    key: 'hue',
    label: 'Hue',
    min: 0,
    max: 360,
    step: 1,
    neutral: 0,
    unit: '°',
    gradient: HUE_TRACK,
    help: 'Colour the mask looks for. Skin sits near 25°, foliage near 110°, sky near 220°.',
  },
  {
    key: 'hueRange',
    label: 'Hue range',
    min: 5,
    max: 180,
    step: 1,
    neutral: 30,
    unit: '°',
    help: 'How far either side of that hue counts. Widen it until the subject is covered, then stop.',
  },
  {
    key: 'minSaturation',
    label: 'Min saturation',
    min: 0,
    max: 1,
    step: 0.01,
    neutral: 0.08,
    help: 'Ignores colours weaker than this, which keeps near-neutral greys out.',
  },
];

/** Three short groups, so the densest panel in the rail can still be scanned. */
type LocalGroup = 'light' | 'colour' | 'detail';

const GROUP_LABEL: Record<LocalGroup, string> = {
  light: 'Light',
  colour: 'Colour',
  detail: 'Detail',
};

interface LocalControl {
  key: keyof LocalAdjustments;
  group: LocalGroup;
  label: string;
  min: number;
  max: number;
  step: number;
  unit?: string;
  help: string;
}

const LOCAL_CONTROLS: LocalControl[] = [
  {
    key: 'exposure',
    group: 'light',
    label: 'Exposure',
    min: -5,
    max: 5,
    step: 0.01,
    unit: ' EV',
    help: 'Brightness inside the mask, in stops. Half a stop usually lifts a face off its background.',
  },
  {
    key: 'contrast',
    group: 'light',
    label: 'Contrast',
    min: -100,
    max: 100,
    step: 1,
    help: 'Mid-tone contrast inside the mask.',
  },
  {
    key: 'highlights',
    group: 'light',
    label: 'Highlights',
    min: -100,
    max: 100,
    step: 1,
    help: 'Pulls back the bright areas inside the mask. The usual fix for a hot forehead.',
  },
  {
    key: 'shadows',
    group: 'light',
    label: 'Shadows',
    min: -100,
    max: 100,
    step: 1,
    help: 'Opens or deepens the dark areas inside the mask.',
  },
  {
    key: 'whites',
    group: 'light',
    label: 'Whites',
    min: -100,
    max: 100,
    step: 1,
    help: 'Moves the white point inside the mask.',
  },
  {
    key: 'blacks',
    group: 'light',
    label: 'Blacks',
    min: -100,
    max: 100,
    step: 1,
    help: 'Moves the black point inside the mask.',
  },
  {
    key: 'temperature',
    group: 'colour',
    label: 'Temperature',
    min: -100,
    max: 100,
    step: 1,
    help: 'Warms or cools the masked area. Keep it modest and watch the edges.',
  },
  {
    key: 'tint',
    group: 'colour',
    label: 'Tint',
    min: -100,
    max: 100,
    step: 1,
    help: 'Green to magenta shift inside the mask.',
  },
  {
    key: 'saturation',
    group: 'colour',
    label: 'Saturation',
    min: -100,
    max: 100,
    step: 1,
    help: 'Every colour inside the mask, up or down.',
  },
  {
    key: 'vibrance',
    group: 'colour',
    label: 'Vibrance',
    min: -100,
    max: 100,
    step: 1,
    help: 'Muted colours first, skin held back, inside the mask.',
  },
  {
    key: 'clarity',
    group: 'detail',
    label: 'Clarity',
    min: -100,
    max: 100,
    step: 1,
    help: 'Mid-size local contrast. Negative on skin, positive on eyes and hair.',
  },
  {
    key: 'texture',
    group: 'detail',
    label: 'Texture',
    min: -100,
    max: 100,
    step: 1,
    help: 'Fine detail inside the mask. The way to soften skin without touching edges.',
  },
  {
    key: 'sharpness',
    group: 'detail',
    label: 'Sharpness',
    min: -100,
    max: 100,
    step: 1,
    help: 'Adds to or backs off the global sharpening, inside the mask only. Judge it at 1:1.',
  },
  {
    key: 'noise',
    group: 'detail',
    label: 'Noise reduction',
    min: -100,
    max: 100,
    step: 1,
    help: 'Cleans one area without softening the whole frame. Judge it at 1:1.',
  },
];

const LOCAL_GROUPS: LocalGroup[] = ['light', 'colour', 'detail'];

export function MasksPanel() {
  const photo = useSelectedPhoto();
  const activeMaskIndex = useStore((state) => state.activeMaskIndex);
  const showMaskOverlay = useStore((state) => state.showMaskOverlay);
  const setShowMaskOverlay = useStore((state) => state.setShowMaskOverlay);
  const setActiveMask = useStore((state) => state.setActiveMask);
  const addMask = useStore((state) => state.addMask);
  const updateMask = useStore((state) => state.updateMask);
  const removeMask = useStore((state) => state.removeMask);
  const commit = useStore((state) => state.commit);

  if (!photo) return null;
  const masks = photo.recipe.masks;
  const index = activeMaskIndex;
  const mask = index >= 0 ? masks[index] : undefined;
  const enabledCount = masks.filter((entry) => entry.enabled).length;

  return (
    <Panel id="masks" title="Local adjustments" active={masks.length > 0}>
      <GroupLabel>
        <Tip
          title="Local adjustments"
          body="The same controls, applied to part of the frame. Add a shape, then edit inside it."
        >
          Add a mask
        </Tip>
      </GroupLabel>
      <div style={ROW} role="group" aria-label="Add a mask">
        {ADD_BUTTONS.map((entry) => (
          <button
            key={entry.kind}
            type="button"
            className="btn tiny"
            disabled={masks.length >= MAX_MASKS}
            onClick={() => addMask(entry.kind)}
          >
            + {entry.label}
          </button>
        ))}
      </div>

      {masks.length >= MAX_MASKS ? (
        <div style={ROW}>
          <span className="notice warn" role="status">
            {MAX_MASKS} masks per photo is the limit
          </span>
        </div>
      ) : null}
      {enabledCount > MAX_MASKS ? (
        <div style={ROW}>
          <span className="notice warn" role="status">
            Only the first {MAX_MASKS} enabled masks render
          </span>
        </div>
      ) : null}

      {masks.length > 0 ? (
        <div style={LIST}>
          {masks.map((entry, entryIndex) => {
            const selected = entryIndex === index;
            return (
              <div key={entry.id} className={`mask-row${selected ? ' on' : ''}`}>
                <input
                  type="checkbox"
                  checked={entry.enabled}
                  aria-label={`${entry.name} enabled`}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    updateMask(entryIndex, (target) => {
                      target.enabled = enabled;
                    }, true);
                  }}
                />
                <button
                  type="button"
                  className="mask-row-name"
                  style={NAME_BUTTON}
                  aria-pressed={selected}
                  onClick={() => setActiveMask(selected ? -1 : entryIndex)}
                >
                  {entry.name}
                </button>
                <span className="label">{KIND_LABEL[entry.kind]}</span>
                {entry.origin === 'ai' ? <span className="mask-origin">AI</span> : null}
                <button
                  type="button"
                  className="btn icon tiny"
                  aria-label={`Delete ${entry.name}`}
                  onClick={() => removeMask(entryIndex)}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      ) : null}

      {mask ? (
        <>
          <label className="mask-row" style={{ marginTop: 4 }}>
            <input
              type="checkbox"
              checked={showMaskOverlay}
              onChange={(event) => setShowMaskOverlay(event.target.checked)}
            />
            <span className="mask-row-name">Show overlay</span>
          </label>

          {maskRationale(mask.origin, mask.rationale)}

          <GroupLabel>{mask.name}</GroupLabel>
          <div style={ROW}>
            <button
              type="button"
              className={`btn tiny${mask.invert ? ' active' : ''}`}
              aria-pressed={mask.invert}
              onClick={() =>
                updateMask(index, (target) => {
                  target.invert = !target.invert;
                }, true)
              }
            >
              {mask.invert ? '✓ ' : ''}Invert
            </button>
            <span className="label">
              {mask.invert ? 'Editing outside the mask' : 'Editing inside the mask'}
            </span>
          </div>
          <Slider
            label="Feather"
            value={mask.feather}
            min={0}
            max={100}
            step={1}
            neutral={50}
            help="Softness of the mask edge. A hard edge across skin always shows."
            onChange={(value) =>
              updateMask(index, (target) => {
                target.feather = value;
              })
            }
            onCommit={commit}
          />
          <Slider
            label="Opacity"
            value={mask.opacity}
            min={0}
            max={100}
            step={1}
            neutral={100}
            help="Strength of the whole layer. Dial the edit in fully, then back it off here."
            onChange={(value) =>
              updateMask(index, (target) => {
                target.opacity = value;
              })
            }
            onCommit={commit}
          />

          <GroupLabel>{KIND_LABEL[mask.kind]} shape</GroupLabel>
          {mask.kind === 'radial'
            ? RADIAL_FIELDS.map((field) => (
                <Slider
                  key={field.key}
                  label={field.label}
                  value={mask.radial[field.key]}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  neutral={field.neutral}
                  unit={field.unit}
                  help={field.help}
                  onChange={(value) =>
                    updateMask(index, (target) => {
                      target.radial[field.key] = value;
                    })
                  }
                  onCommit={commit}
                />
              ))
            : null}
          {mask.kind === 'linear'
            ? LINEAR_FIELDS.map((field) => (
                <Slider
                  key={field.key}
                  label={field.label}
                  value={mask.linear[field.key]}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  neutral={field.neutral}
                  help={field.help}
                  onChange={(value) =>
                    updateMask(index, (target) => {
                      target.linear[field.key] = value;
                    })
                  }
                  onCommit={commit}
                />
              ))
            : null}
          {mask.kind === 'luminance'
            ? LUMINANCE_FIELDS.map((field) => (
                <Slider
                  key={field.key}
                  label={field.label}
                  value={mask.luminance[field.key]}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  neutral={field.neutral}
                  help={field.help}
                  onChange={(value) =>
                    updateMask(index, (target) => {
                      target.luminance[field.key] = value;
                    })
                  }
                  onCommit={commit}
                />
              ))
            : null}
          {mask.kind === 'color'
            ? COLOR_FIELDS.map((field) => (
                <Slider
                  key={field.key}
                  label={field.label}
                  value={mask.color[field.key]}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  neutral={field.neutral}
                  unit={field.unit}
                  gradient={field.gradient}
                  help={field.help}
                  onChange={(value) =>
                    updateMask(index, (target) => {
                      target.color[field.key] = value;
                    })
                  }
                  onCommit={commit}
                />
              ))
            : null}

          {LOCAL_GROUPS.map((group) => (
            <Fragment key={group}>
              <GroupLabel>{GROUP_LABEL[group]}</GroupLabel>
              {LOCAL_CONTROLS.filter((control) => control.group === group).map((control) => (
                <Slider
                  key={control.key}
                  label={control.label}
                  value={mask.adjustments[control.key]}
                  min={control.min}
                  max={control.max}
                  step={control.step}
                  neutral={0}
                  unit={control.unit}
                  help={control.help}
                  onChange={(value) =>
                    updateMask(index, (target) => {
                      target.adjustments[control.key] = value;
                    })
                  }
                  onCommit={commit}
                />
              ))}
            </Fragment>
          ))}
        </>
      ) : null}
    </Panel>
  );
}

/** The planner's stated reason for a mask it proposed, kept next to the mask itself. */
function maskRationale(origin: 'manual' | 'ai', rationale: string | undefined) {
  if (origin !== 'ai' || !rationale) return null;
  return (
    <div className="slider-help">
      <span className="mask-origin">Proposed by Lumos</span> {rationale}
    </div>
  );
}
